<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { prepareDashboardSessionApi } from '@Api/invitation.api';
import { getBasePath } from '@Src/mfe.config';

const router = useRouter();
const route = useRoute();

const isLoading = ref(true);
const isInvalid = ref(false);

onMounted(async () => {
    const token = route.query.token as string | undefined;

    if (!token) {
        isInvalid.value = true;
        isLoading.value = false;
        return;
    }

    try {
        // PrepareDashboardSession flips participant.Status = "active" AND patches the activity
        // session with ClientId + recipient details (phone/email) so the subsequent auth flow
        // can build a LoginHint and show the OTP entry form.
        const result = await prepareDashboardSessionApi(token);

        if (result.status === 'ready') {
            // Mark that this session entered via the dashboard invite flow so
            // the host's ContinueFlowView can prefer dashboard over the default
            // payments redirect when mfeRoutePath is lost (PS-939).
            sessionStorage.setItem('clienthub:dashboardSession', 'true');

            const basePath = getBasePath() ?? '';
            const dealPath = result.dealId ? `/deal/${result.dealId}` : '';

            // PS-1144 — the Request BFF appends `taskId` when the invite resolves to a
            // specific deal task (a finalize-followup email links to one task, not the
            // list). Carry it onto the dashboard route so TaskView can open that task.
            // Absent for plain invites, which keep landing on the list as before.
            const taskId = route.query.taskId as string | undefined;
            const taskQuery = taskId ? `?taskId=${encodeURIComponent(taskId)}` : '';

            router.replace(`${basePath}/dashboard${dealPath}${taskQuery}`);
            return;
        }

        isInvalid.value = true;
    } catch {
        isInvalid.value = true;
    } finally {
        isLoading.value = false;
    }
});
</script>

<template>
    <div class="invite-view">
        <app-page-loader v-if="isLoading" />

        <div v-else-if="isInvalid" class="invite-view__error">
            <h2>Invitation no longer valid</h2>
            <p>This invitation link has expired or is no longer active. Please contact your title company for a new invitation.</p>
        </div>
    </div>
</template>

<style lang="scss" scoped>
@use 'certifid-v2-client-hub-styles' as *;

.invite-view {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 100%;
    padding: 2rem;
    background: $portal-bg;

    &__error {
        text-align: center;
        max-width: 24.5rem;
        background-color: $portal-surface;
        border: 1px solid $portal-border;
        border-radius: $portal-card-radius;
        box-shadow: $portal-card-shadow;
        padding: $portal-card-padding;

        h2 {
            font-size: $text-portal-h4;
            font-weight: 700;
            color: $portal-text;
            margin: 0 0 0.5rem;
        }

        p {
            font-size: $text-portal-caption;
            color: $portal-text-mid;
            line-height: 150%;
            margin: 0;
        }
    }
}
</style>
